/**
 * Professional logging utility for TraderTekno
 * Replaces console.log statements with structured logging
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  context?: Record<string, unknown>;
  error?: Error;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private logLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.INFO;

  private formatMessage(level: LogLevel, message: string, context?: Record<string, unknown>, error?: Error): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level: LogLevel[level],
      message,
      context,
      error,
    };
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  private outputLog(entry: LogEntry): void {
    if (!this.isDevelopment) {
      // In production, send to monitoring service
      // For now, we'll suppress output
      return;
    }

    const { timestamp, level, message, context, error } = entry;
    const prefix = `[${timestamp}] ${level}:`;

    switch (level) {
      case 'DEBUG':
        // eslint-disable-next-line no-console
        console.debug(prefix, message, context ?? '', error ?? '');
        break;
      case 'INFO':
        // eslint-disable-next-line no-console
        console.info(prefix, message, context ?? '', error ?? '');
        break;
      case 'WARN':
        console.warn(prefix, message, context ?? '', error ?? '');
        break;
      case 'ERROR':
        // eslint-disable-next-line no-console
        console.error(prefix, message, context ?? '', error ?? '');
        break;
    }
  }

  debug(message: string, context?: Record<string, unknown>): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      this.outputLog(this.formatMessage(LogLevel.DEBUG, message, context));
    }
  }

  info(message: string, context?: Record<string, unknown>): void {
    if (this.shouldLog(LogLevel.INFO)) {
      this.outputLog(this.formatMessage(LogLevel.INFO, message, context));
    }
  }

  warn(message: string, context?: Record<string, unknown>): void {
    if (this.shouldLog(LogLevel.WARN)) {
      this.outputLog(this.formatMessage(LogLevel.WARN, message, context));
    }
  }

  error(message: string, context?: Record<string, unknown>, error?: Error): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      this.outputLog(this.formatMessage(LogLevel.ERROR, message, context, error));
    }
  }

  // Performance logging
  timeStart(label: string): void {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console
      console.time(label);
    }
  }

  timeEnd(label: string): void {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console
      console.timeEnd(label);
    }
  }

  // API request logging
  apiRequest(method: string, url: string, context?: Record<string, unknown>): void {
    this.debug(`API ${method.toUpperCase()}: ${url}`, context);
  }

  apiResponse(method: string, url: string, status: number, context?: Record<string, unknown>): void {
    const level = status >= 400 ? LogLevel.ERROR : LogLevel.DEBUG;
    const message = `API ${method.toUpperCase()}: ${url} - ${status}`;
    
    if (level === LogLevel.ERROR) {
      this.error(message, context);
    } else {
      this.debug(message, context);
    }
  }

  // Authentication logging
  authEvent(event: string, context?: Record<string, unknown>): void {
    this.info(`Auth: ${event}`, context);
  }

  // Trading signal logging
  signalEvent(event: string, context?: Record<string, unknown>): void {
    this.info(`Signal: ${event}`, context);
  }

  // Payment logging
  paymentEvent(event: string, context?: Record<string, unknown>): void {
    this.info(`Payment: ${event}`, context);
  }
}

// Export singleton instance
export const logger = new Logger();

// Convenience exports for common use cases
export const logApi = {
  request: logger.apiRequest.bind(logger),
  response: logger.apiResponse.bind(logger),
};

export const logAuth = logger.authEvent.bind(logger);
export const logSignal = logger.signalEvent.bind(logger);
export const logPayment = logger.paymentEvent.bind(logger);